import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { sectionStyles } from '../styles/globalStyles';

import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
    HiOutlineWindow,
    HiOutlineSwatch,
    HiOutlineBriefcase,
    HiOutlineChartPie
} from 'react-icons/hi2';
import { RiLightbulbFlashLine } from "react-icons/ri";

const COMMON_ICON_PROPS = { className: "w-5 h-5 inline mr-1" };

const SkillCard = React.memo(({ skill, isExpanded, onToggle, t }) => {
    const { IconComponent, title, description, cv, id } = skill;
    const contentId = `skill-cv-${id}`; 

    return (
        <div className="skill-item p-8 md:p-12 rounded"> 
            <h2 className="text-xl font-semibold text-gray-800 mb-4 dark:text-gray-200">
                {IconComponent && <IconComponent {...COMMON_ICON_PROPS} />} {title}
            </h2>
            <p className="text-gray-600 dark:text-gray-200 mb-2">
                {description}
            </p>
            <p
                id={contentId}
                className={`italic border-l-2 pl-4 text-xs mt-4 text-gray-600 dark:text-gray-200 ${
                    isExpanded ? "" : "hidden"
                }`}
            >
                {cv}
            </p>
            <button
                type="button"
                onClick={onToggle}
                className="flex flex-row items-center justify-center text-xs font-light py-2 cursor-pointer rounded gap-2 w-full mt-4" 
                aria-expanded={isExpanded}
                aria-controls={contentId}
            >
                {isExpanded ? t('skill.less') : t('skill.more')}
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>
        </div>
    );
});

const SKILL_DEFINITIONS = [
    { id: 'uiux', IconComponent: HiOutlineSwatch, translationBase: 'skill.uiux', column: 'left' },
    { id: 'analytics', IconComponent: HiOutlineChartPie, translationBase: 'skill.analytics', column: 'left' },
    { id: 'frontend', IconComponent: HiOutlineWindow, translationBase: 'skill.frontend', column: 'right' },
    { id: 'productowner', IconComponent: HiOutlineBriefcase, translationBase: 'skill.productowner', column: 'right' },
    { id: 'digitaltransformation', IconComponent: RiLightbulbFlashLine, translationBase: 'skill.digitaltransformation', column: 'right' },
];

const Skill = () => {
    const { t } = useTranslation();
    const [expandedSkills, setExpandedSkills] = useState({});

    const processedSkills = useMemo(() => {
        return SKILL_DEFINITIONS.map(skillDef => ({
            ...skillDef,
            title: t(`${skillDef.translationBase}.title`),
            description: t(`${skillDef.translationBase}.description`),
            cv: t(`${skillDef.translationBase}.cv`),
        }));
    }, [t]);

    const skillsLeft = processedSkills.filter(skill => skill.column === 'left');
    const skillsRight = processedSkills.filter(skill => skill.column === 'right');

    const toggleSkillExpansion = useCallback((skillId) => {
        setExpandedSkills(prev => ({
            ...prev,
            [skillId]: !prev[skillId],
        }));
    }, []); 

    return (
        <section id="skills" className={sectionStyles.section}>
            <div className={sectionStyles.container}>
                <div className={sectionStyles.title}>
                    <p className={sectionStyles.breadcrumb}>
                        <ChevronRightIcon className="w-4 h-4 mr-2" />
                        {t('skill.title')}
                    </p>
                    <h2 className={sectionStyles.heading}>
                        {t('skill.subtitle')}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="flex flex-col space-y-12 mt-0 md:mt-44">
                        {skillsLeft.map(skill => (
                            <SkillCard
                                key={skill.id}
                                skill={skill}
                                isExpanded={!!expandedSkills[skill.id]}
                                onToggle={() => toggleSkillExpansion(skill.id)}
                                t={t} 
                            />
                        ))}
                    </div>

                    <div className="flex flex-col space-y-12">
                        {skillsRight.map(skill => (
                            <SkillCard
                                key={skill.id}
                                skill={skill}
                                isExpanded={!!expandedSkills[skill.id]}
                                onToggle={() => toggleSkillExpansion(skill.id)}
                                t={t}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Skill;